import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { FilterFieldNames, ILeafFilterClause } from '~src/spacesPlatform/models/IFilterClause';

import {
  GetContentRelayPlanOwnersRequest,
  IContentRelayPlanOwner,
  IGetContentRelayPlanOwnersResult,
} from '../serverApi/requests/GetContentRelayPlanOwnersRequest';
import { hasErrors } from '../utils/resultErrors';
import { LIST_PLANS_MAX_PAGE_SIZE } from './ContentRelayPlansResourceQuery';

/**
 * Filter panel typeahead values for the Content Relay plan list. getFieldValues doesn't know plans, so the
 * values come from the monolith `listPlanOwners` web method: every user who owns plans on the site, with
 * their plan count, ordered by name, and matched and paged server-side. Only the owner field is supported.
 *
 * The owners and counts cover every plan on the site: listPlanOwners does not take the plan list's other
 * filters, so the active filter clauses do not narrow the values.
 */
export class ContentRelayPlanFieldValuesQuery {

  private filterClauses: ILeafFilterClause[] = [];
  private matchText: string = '';

  constructor(private field: string) { }

  public getSlice(start: number, size: number): Promise<ListResult<IFieldValue>> {
    if (this.field !== FilterFieldNames.ownerId) {
      return Promise.resolve(ListResult.empty<IFieldValue>());
    }
    const from = start || 0;
    return WebHttpClient.sendRequest(new GetContentRelayPlanOwnersRequest({
      matchText: this.matchText || undefined,
      pageSize: Math.min(size, LIST_PLANS_MAX_PAGE_SIZE),
      startIndex: from,
    }))
      .then((result: IGetContentRelayPlanOwnersResult) => {
        if (!result || hasErrors(result)) {
          return Promise.reject(result);
        }
        const owners = result.owners || [];
        const total = result.totalCount || 0;
        const values: IFieldValue[] = owners
          // An owner without a name (no system user) can't be shown in the list.
          .filter((owner: IContentRelayPlanOwner) => !!owner.name)
          .map((owner: IContentRelayPlanOwner) => ({ value: String(owner.userId), label: owner.name, count: owner.planCount }));
        // An empty page ends the list even if totalCount says more, so the panel can't keep asking for it.
        return new ListResult<IFieldValue>(values, from, total, [], owners.length > 0 && from + owners.length < total);
      });
  }

  public getFilters(): ILeafFilterClause[] {
    return this.filterClauses;
  }

  public withMatchText(matchText: string): ContentRelayPlanFieldValuesQuery {
    const query = this.clone();
    query.matchText = matchText;
    return query;
  }

  public withFilters(filterClauses: ILeafFilterClause[]): ContentRelayPlanFieldValuesQuery {
    const query = this.clone();
    query.filterClauses = filterClauses;
    return query;
  }

  private clone(): ContentRelayPlanFieldValuesQuery {
    const query = new ContentRelayPlanFieldValuesQuery(this.field);
    query.filterClauses = this.filterClauses;
    query.matchText = this.matchText;
    return query;
  }
}
